import axios from '../../../utils/api/axiosInstance';
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import dayjs, { Dayjs } from "dayjs";

import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";

import ClearIcon from "@mui/icons-material/Clear";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';

import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";

import { DateTimeRangePicker } from '@mui/x-date-pickers-pro/DateTimeRangePicker';
import { MultiInputDateTimeRangeField } from '@mui/x-date-pickers-pro/MultiInputDateTimeRangeField';

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

function LogsView() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [error, setError] = useState<string | null>(null);

  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [openRange, setOpenRange] = useState(false);
  const [from, to] = range;

  const [search, setSearch] = useState("");

  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const [sources, setSources] = useState<string[]>([]);
  const [visibleSources, setVisibleSources] = useState<string[]>([]);
  const [logLevels, setLogLevels] = useState<string[]>([]);
  const [logLevelColors, setLogLevelColors] = useState<Record<string, string>>({});
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  const handleBackClick = () => {
    navigate('/');
  };

  const [visibleLevels, setVisibleLevels] = useState<string[]>([]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: String(page),
        size: String(pageSize),
      });

      if (from) params.append('from', from.toISOString());
      if (to) params.append('to', to.toISOString());
      if (visibleLevels.length) params.append('levels', visibleLevels.join(','));
      if (search) params.append('search', search);
      if (visibleSources.length) params.append('sources', visibleSources.join(','));

      /* const token = localStorage.getItem('userToken'); */

      const res = await axios.get('/api/logs', { params });

      const adaptedLogs = res.data.logs.map((log: any) => ({
        id: log.id,
        message: log.message,
        source: log.source,
        level: log.logLevel,
        color: log.color,
        timestamp: log.timestamp,
      }));

      setLogs(adaptedLogs);
      setRowCount(res.data.total);
      setLoading(false);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate(`/auth?redirect=${encodeURIComponent('/logs')}`);
      } else {
        setError(err.message || 'Failed to save configuration');
        setLogs([]);
        setRowCount(0);
        setLoading(false);
      }
    }
  }, [from, to, visibleLevels, visibleSources, search, page, pageSize]);

  const handleClearFilters = () => {
    setRange([null, null]);
    setVisibleLevels([]);
    setVisibleSources([]);
    setSearch('');
    setPage(0);
  };

  const hasFilters =
    from ||
    to ||
    visibleLevels.length > 0 ||
    visibleSources.length > 0 ||
    search.length > 0;

  const fetchLogsConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      /* const token = localStorage.getItem('userToken'); */

      const res = await axios.get('/api/logs/config');

      setLogLevels(res.data.levels);
      setLogLevelColors(res.data.colors);
      setSources(res.data.sources);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate(`/auth?redirect=${encodeURIComponent('/logs')}`);
      } else {
        setError(err.message || 'Failed to load logs configuration');
      }
    }
  }, []);

  const columns: GridColDef[] = [
    {
      field: "level",
      headerName: t('logsview.logLevel'),
      minWidth: 120,
      flex: 0,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const { level, color } = params.row;
        return (
          <Chip
            label={level ?? 'UNKNOWN'}
            size="small"
            sx={{
              backgroundColor: color || "gray",
              color: "white",
              fontWeight: 600,
              minWidth: 70,
            }}
          />
        );
      },
    },
    {
      field: "source",
      headerName: t('logsview.source'),
      minWidth: 140,
      flex: 0,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      ),
    },
    {
      field: "message",
      headerName: t('logsview.message'),
      minWidth: 300,
      flex: 1,
    },
    {
      field: "timestamp",
      headerName: t('logsview.dateHour'),
      minWidth: 180,
      flex: 0.3,
      valueFormatter: (value: string) =>
        dayjs(value).format("DD/MM/YYYY HH:mm:ss"),
    },
  ];

  const shortcutsItems = [
    {
      label: t('logsview.last_hour'),
      getValue: () => {
        const now = dayjs();
        return [
          now.subtract(1, 'hour'),
          now,
        ] as [Dayjs, Dayjs];
      },
    },
    {
      label: t('logsview.last_day'),
      getValue: () => {
        const now = dayjs();
        return [
          now.subtract(1, 'day'),
          now,
        ] as [Dayjs, Dayjs];
      },
    },
    {
      label: t('logsview.last_week'),
      getValue: () => {
        const now = dayjs();
        return [
          now.subtract(7, 'day'),
          now,
        ] as [Dayjs, Dayjs];
      },
    },
    {
      label: t('logsview.this_month'),
      getValue: () => {
        const now = dayjs();
        return [
          now.startOf('month'),
          now.endOf('month'),
        ] as [Dayjs, Dayjs];
      },
    },
    {
      label: t('logsview.last_month'),
      getValue: () => {
        const now = dayjs();
        const lastMonth = now.subtract(1, 'month');
        return [
          lastMonth.startOf('month'),
          lastMonth.endOf('month'),
        ] as [Dayjs, Dayjs];
      },
    },
    {
      label: t('logsview.reset'),
      getValue: () => [null, null] as [null, null],
    },
  ];

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchLogsConfig();
  }, [fetchLogsConfig]);

  useEffect(() => {
    setPage(0);
  }, [from, to, visibleLevels, visibleSources, search]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Title */}
        <Box
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            minHeight: '67px'
          }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon
              sx={{ fontSize: '0.75rem' }}
            />}
            aria-label="breadcrumb"
            sx={{ ml: 4 }}
          >
            <Link
              underline="hover"
              color={theme.palette.text.secondary}
              sx={{ fontWeight: 450, fontSize: '0.875rem', cursor: 'pointer' }}
              onClick={handleBackClick}
            >
              ← {t('mdm.backToDashboard')}
            </Link>
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {t('logsview.logsview')}
            </Typography>
          </Breadcrumbs>
        </Box>

        {error && (
          <Alert severity="error">
            {error}
          </Alert>
        )}

        {!error && (
          <Box sx={{ p: 4, mt: 4 }}>
            {/* Filters */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mb: 1,
                alignItems: "center",
                "& > *": {
                  minWidth: { xs: "100%", sm: "auto" },
                },
              }}
            >
              <Autocomplete
                sx={{ minWidth: '160px' }}
                multiple
                disableCloseOnSelect
                limitTags={2}
                size="small"
                options={sources}
                value={visibleSources}
                onChange={(_, newValue) => setVisibleSources(newValue)}
                renderOption={(props, source) => (
                  <li {...props}>
                    <Chip
                      label={source}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontWeight: 500,
                      }}
                    />
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((source, index) =>
                    index < 2 ? (
                      <Chip
                        {...getTagProps({ index })}
                        key={source}
                        label={source}
                        size="small"
                        variant="outlined"
                        sx={{ height: 24, fontWeight: 500 }}
                      />
                    ) : null
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('logsview.source')}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />

              <Autocomplete
                sx={{ minWidth: '128px' }}
                multiple
                disableCloseOnSelect
                limitTags={2}
                size="small"
                options={logLevels}
                value={visibleLevels}
                onChange={(_, newValue) => setVisibleLevels(newValue)}
                renderOption={(props, level) => (
                  <li {...props}>
                    <Chip
                      label={level}
                      size="small"
                      sx={{
                        backgroundColor: logLevelColors[level] ?? '#6a6a6a',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((level, index) =>
                    index < 2 ? (
                      <Chip
                        {...getTagProps({ index })}
                        key={level}
                        label={level}
                        size="small"
                        sx={{
                          backgroundColor: logLevelColors[level] ?? '#6a6a6a',
                          color: 'white',
                          fontWeight: 600,
                          height: 24,
                        }}
                      />
                    ) : null
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('logsview.logLevels')}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />

              <DateTimeRangePicker
                value={range}
                onChange={(newValue) => setRange(newValue)}
                open={openRange}
                onOpen={() => setOpenRange(true)}
                onClose={() => setOpenRange(false)}
                format="DD/MM/YYYY HH:mm"
                slots={{
                  field: MultiInputDateTimeRangeField,
                }}
                slotProps={{
                  shortcuts: {
                    items: shortcutsItems,
                  },
                  field: {
                    readOnly: true,
                    onClick: () => setOpenRange(true),
                  },
                  textField: { size: "small" },
                }}
              />

              {hasFilters && (
                <Button
                  variant="contained"
                  color="inherit"
                  startIcon={<ClearIcon fontSize="small" />}
                  onClick={handleClearFilters}
                  sx={{
                    height: 36,
                    textTransform: "none",
                    fontWeight: 450,
                  }}
                >
                  {t('logsview.clean')}
                </Button>
              )}

              <TextField
                size="small"
                label={t('logsview.searchMessage')}
                placeholder={t('logsview.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ ml: "auto", minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Table */}
            <DataGridPro
              rows={logs}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              disableColumnMenu
              pagination
              paginationMode="server"
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={rowCount}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => {
                setPage(model.page);
                setPageSize(model.pageSize);
              }}
              onRowDoubleClick={(params) => {
                setSelectedLog(params.row);
                setOpenModal(true);
              }}
              sx={{
                minWidth: '400px',
                minHeight: '232px',
                maxHeight: 'calc(100vh - 336px)',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                }
              }}
            />
          </Box>
        )}
      </Box>

      {/* Modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('logsview.log')} #{selectedLog?.id}</DialogTitle>

        <DialogContent dividers>
          <Typography
            variant="body1"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {selectedLog?.message}
          </Typography>

          <Typography sx={{ mt: 2, fontSize: 12, opacity: 0.7 }}>
            {t('logsview.logLevel')}: {selectedLog?.level}
            <br />
            {t('logsview.dateHour')}: {dayjs(selectedLog?.timestamp).format("DD/MM/YYYY HH:mm:ss")}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>{t('logsview.close')}</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

export default LogsView;